/*
 * Copyright (c) Nexign, JSC, 2019-2020.
 */
/**
 * Selectors for page ExpensesAnalyzer
 *
 * @author Fedorov Maxim
 * @author Yakov Gushchin
 * @author Evgenij Tyan
 * @author Anton Panasenko
 * @author Aleksandr Perkhunov
 * @version 1.3.0
 * @since 1.1.0
 */

import { createSelector, OutputSelector } from 'reselect';

import { IChartCategoryRange, IChartDataInput, IChartLabels } from '../../components/common/Chart/chartTypes';
import { SIDE_BAR_SELECT_ALL_ITEM } from '../../components/common/SideBar/SideBarConstants';
import { IRootReducer } from '../../reducers/rootReducer';
import { EExpensesAnalyzerChart } from './components/ExpensesAnalyzerController/expensesAnalyzerControllerTypes';
import {
    IExpensesAnalyzerApiPath,
    MapExpensesAnalyzerTypeToApiPath,
} from './components/ExpensesAnalyzerTable/types/expensesAnalyzerTableMapTypes';
import { IPageExpensesAnalyzerReducer } from './pageExpensesAnalyzerReducer';
import { MapExpensesAnalyzerTypeToMeta } from './types/pageExpensesAnalyzerMapTypes';
import { EExpensesAnalyzerTypes, IExpensesAccountsListItem } from './types/pageExpensesAnalyzerTypes';

export const getExpensesAnalyzer = (state: IRootReducer): IPageExpensesAnalyzerReducer =>
    state.expensesAnalyzer;

/*
* -------------------------------------------
*        GET ANALYZER TYPE
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerType =
    (): OutputSelector<IRootReducer, EExpensesAnalyzerTypes, (state: IPageExpensesAnalyzerReducer) =>
        EExpensesAnalyzerTypes> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.expensesAnalyzerType,
        );

/*
* -------------------------------------------
*           IS GRAPH SHOW
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerShowGraph =
    (): OutputSelector<IRootReducer, boolean, (state: IPageExpensesAnalyzerReducer) => boolean> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.isShowGraph,
        );

/*
* -------------------------------------------
*        CURRENT ACCOUNT
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerCurrentAccount =
    (): OutputSelector<IRootReducer, number, (state: IPageExpensesAnalyzerReducer) => number> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.currentAccount,
        );

export const makeSelectorExpensesAnalyzerSelectedId =
    (): OutputSelector<IRootReducer, number, (state: IPageExpensesAnalyzerReducer) => number> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.selectedId,
        );

/*
* -------------------------------------------
*        CURRENT CHART
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerCurrentChart =
    (): OutputSelector<IRootReducer, EExpensesAnalyzerChart,
        (state: IPageExpensesAnalyzerReducer) => EExpensesAnalyzerChart> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.currentChart,
        );

/*
* -------------------------------------------
*        CHART DATA
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerCategoryData =
    (): OutputSelector<IRootReducer, IChartDataInput[], (state: IPageExpensesAnalyzerReducer) => IChartDataInput[]> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.chartData[EExpensesAnalyzerChart.CATEGORY],
        );

export const makeSelectorExpensesAnalyzerDynamicData =
    (): OutputSelector<IRootReducer, IChartDataInput[], (state: IPageExpensesAnalyzerReducer) => IChartDataInput[]> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.chartData[EExpensesAnalyzerChart.DYNAMIC],
        );

export const makeSelectorExpensesAnalyzerCurrentChartData =
    (): OutputSelector<IRootReducer, IChartDataInput[], (state: IPageExpensesAnalyzerReducer) => IChartDataInput[]> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.chartData[state.currentChart],
        );

/*
* -------------------------------------------
*        CHART IS LOADING
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerChartIsLoading =
    (): OutputSelector<IRootReducer, boolean, (state: IPageExpensesAnalyzerReducer) => boolean> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.isChartLoading,
        );

/*
* -------------------------------------------
*        CHART ERROR
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerError =
    (): OutputSelector<IRootReducer, string, (state: IPageExpensesAnalyzerReducer) => string> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.error,
        );


/*
* -------------------------------------------
*        CHART LABELS
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerChartLabels =
    (): OutputSelector<IRootReducer, IChartLabels, (state: IPageExpensesAnalyzerReducer) =>
        IChartLabels> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.chartLabels,
        );

/*
* -------------------------------------------
*        CHART CATEGORY RANGE
* -------------------------------------------
*/

export const makeSelectorExpensesAnalyzerChartCategoryRange =
    (): OutputSelector<IRootReducer, IChartCategoryRange, (state: IPageExpensesAnalyzerReducer) =>
        IChartCategoryRange> =>
        createSelector(
            getExpensesAnalyzer,
            (state: IPageExpensesAnalyzerReducer) => state.chartCategoryRange,
        );

/*
* -------------------------------------------
*        GET ACCOUNTS LIST
* -------------------------------------------
*/

export const makeSelectorExpensesAccountsList =
    (): OutputSelector<IRootReducer, IExpensesAccountsListItem[], (state: IPageExpensesAnalyzerReducer) =>
        IExpensesAccountsListItem[]> =>
        createSelector(
            getExpensesAnalyzer,
            state => state.accountsList,
        );

export const makeSelectorExpensesAccountsListFulled =
    (): OutputSelector<IRootReducer, boolean, (state: IPageExpensesAnalyzerReducer) => boolean> =>
        createSelector(
            getExpensesAnalyzer,
            state => state.accountsListFulled,
        );

export const makeSelectorExpensesAccountsApiPathConfiguration =
    (): OutputSelector<IRootReducer, IExpensesAnalyzerApiPath, (state: IPageExpensesAnalyzerReducer) =>
        IExpensesAnalyzerApiPath> =>
        createSelector(
            getExpensesAnalyzer,
            state => {
                const type = state.expensesAnalyzerType;
                const id = state.selectedId || state.currentAccount;
                if (!id || state.selectedId === SIDE_BAR_SELECT_ALL_ITEM) {
                    return MapExpensesAnalyzerTypeToApiPath[type].all
                        || MapExpensesAnalyzerTypeToApiPath[type].common;
                } else {
                    return MapExpensesAnalyzerTypeToApiPath[type].common;
                }
            },
        );

export const makeSelectorExpensesIsTableAccount = (): OutputSelector<IRootReducer, boolean,
    (state: IPageExpensesAnalyzerReducer) => boolean> => createSelector(getExpensesAnalyzer,
    state => MapExpensesAnalyzerTypeToMeta[state.expensesAnalyzerType].isTableAccount ?? false);
